import { useEffect, useRef, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { hexToRgba } from "@aclymatepackages/converters";

const MIN_AREA_PX = 400;

const computeRect = (start, current) => ({
  x: Math.min(start.x, current.x),
  y: Math.min(start.y, current.y),
  w: Math.abs(current.x - start.x),
  h: Math.abs(current.y - start.y)
});

const SnipOverlay = ({ containerRef, onComplete, onCancel }) => {
  const { palette } = useTheme();
  const overlayRef = useRef(null);
  const [drag, setDrag] = useState(null);

  useEffect(() => {
    const previousCursor = document.body.style.cursor;
    document.body.style.cursor = "crosshair";
    return () => {
      document.body.style.cursor = previousCursor;
    };
  }, []);

  const eventCoords = (event) => {
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) {
      return { x: event.clientX, y: event.clientY };
    }
    return {
      x: event.clientX - containerRect.left,
      y: event.clientY - containerRect.top
    };
  };

  const handleMouseDown = (event) => {
    event.preventDefault();
    const point = eventCoords(event);
    setDrag({ start: point, current: point });
  };

  const handleMouseMove = (event) => {
    if (!drag) {
      return;
    }
    const point = eventCoords(event);
    setDrag({ start: drag.start, current: point });
  };

  const handleMouseUp = () => {
    if (!drag) {
      return;
    }
    const rect = computeRect(drag.start, drag.current);
    setDrag(null);
    if (rect.w * rect.h < MIN_AREA_PX) {
      onCancel();
      return;
    }
    onComplete(rect);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setDrag(null);
      onCancel();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  const rect = drag ? computeRect(drag.start, drag.current) : null;

  return (
    <Box
      ref={overlayRef}
      data-snip-exclude="true"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      sx={{
        position: "absolute",
        inset: 0,
        cursor: "crosshair",
        zIndex: 1200,
        userSelect: "none"
      }}
    >
      {rect && (
        <Box
          sx={{
            position: "absolute",
            left: rect.x,
            top: rect.y,
            width: rect.w,
            height: rect.h,
            border: `2px dashed ${palette.primary.main}`,
            backgroundColor: hexToRgba(palette.primary.main, 0.12),
            pointerEvents: "none"
          }}
        />
      )}
    </Box>
  );
};

export default SnipOverlay;
